<script lang="ts" setup>
  // Validation occurs:
  // - Validate on Change/Blur initially (when the user leaves the control), let’s call this lazy mode.
  // - If the field is invalid, switch the validation to validate on input (when the user types), let’s call this aggressive mode.
  // - If the field is valid, go back to “lazy” mode, otherwise, be “aggressive”.
  // See https://vee-validate.logaretm.com/v4/guide/composition-api/custom-inputs#handling-events

  import InputNumber, { type InputNumberBlurEvent, type InputNumberInputEvent } from 'primevue/inputnumber'
  import { camelCaseToKebabCase } from '~/utils/string'

  const props = withDefaults(
    defineProps<{
      disabled?: boolean
      label: string
      name: string
      max?: number
      min?: number
      prefix?: string
      validateInitialValue?: boolean
    }>(),
    {
      disabled: false,
      max: undefined,
      min: undefined,
      prefix: undefined
    }
  )

  const emits = defineEmits<{
    (event: 'blur', payload: Event): void
    (event: 'input', payload: Event): void
  }>()

  const inputElement = ref<InstanceType<typeof InputNumber> | null>(null)
  defineExpose({ inputElement })

  const selectorName = computed(() => camelCaseToKebabCase(props.name))

  const { value, errorMessage, handleBlur, validate, meta } = useField<number | null | undefined>(
    () => props.name,
    undefined,
    {
      validateOnValueUpdate: true
    }
  )

  onMounted(() => {
    // If there is an initial value on mount, and the field is not validated, validate it.
    if (props.validateInitialValue && !(value.value === undefined || value.value === null) && !meta.validated) {
      validate()
    }
  })

  function onBlur(event: InputNumberBlurEvent) {
    emits('blur', event.originalEvent)
    handleBlur(event.originalEvent, false)
  }

  function onInput(event: InputNumberInputEvent) {
    emits('input', event.originalEvent)
  }
</script>

<template>
  <div class="c-input-number-field">
    <FloatLabel variant="in">
      <InputNumber :id="selectorName" v-model="value" :max :min :prefix fluid @blur="onBlur" @input="onInput" />

      <label :class="{ 'invalid': !!errorMessage }" :for="selectorName">{{ label }}</label>
    </FloatLabel>
    <small v-if="errorMessage" :id="`${selectorName}-error`" class="input-error-message">
      {{ errorMessage }}
    </small>
  </div>
</template>

<style lang="scss" scoped>
  @import '../styles/form.scss';
</style>
